import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { timeout } from "rxjs/operators";
import { environment } from "../../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class FormatosService {
  private apiUrl = `${environment.apiUrl}/api/formatos`;
  private templatesUrl = `${environment.apiUrl}/api/templates`;
  private readonly requestTimeoutMs = 60000;

  constructor(private http: HttpClient) {}

  // ==================== RUTAS CRUD ====================

  // GET /formatos - Obtener todos los formatos
  getAll(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}`, { withCredentials: true });
  }

  // GET /formatos/activos - Obtener formatos activos
  getActivos(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/activos`, { withCredentials: true });
  }

  // GET /formatos/:id - Obtener formato por ID
  getById(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${id}`, { withCredentials: true });
  }

  // POST /formatos - Crear nuevo formato
  create(formato: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}`, formato, { withCredentials: true });
  }

  // PUT /formatos/:id - Actualizar formato
  update(id: number | string, formato: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}`, formato, { withCredentials: true });
  }

  // DELETE /formatos/:id - Eliminar formato
  remove(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, { withCredentials: true });
  }

  // PUT /formatos/:id/inactivar - Inactivar formato
  inactivar(id: number | string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}/inactivar`, {}, { withCredentials: true });
  }

  // PATCH /formatos/:id/activate - Activar/desactivar formato
  activate(id: number | string, activo: boolean): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/${id}/activate`, { activo }, { withCredentials: true });
  }

  // ==================== RUTAS DE GENERACIÓN DE DOCUMENTOS ====================

  private normalizarPayloadDocumento(datos: any): any {
    const payload = { ...(datos || {}) };

    const version =
      payload.version ??
      payload.numero_version ??
      payload.numeroVersion ??
      payload.codigo ??
      payload.versioN ??
      "";

    const fecha =
      payload.fecha ??
      payload.fecha_designacion ??
      payload.fecha_aprobacion ??
      payload.fechaPublicacion ??
      payload.fecha_publicacion ??
      "";

    if (version !== "") {
      payload.version = version;
    }

    if (fecha !== "") {
      payload.fecha = fecha;
      payload.fechaPublicacion = payload.fechaPublicacion ?? fecha;
    }

    if (payload.documento_id === undefined && payload.documentoId !== undefined) {
      payload.documento_id = payload.documentoId;
    }

    try {
      console.log("[FormatosService] payload documento normalizado", {
        versionOriginal: datos?.version ?? datos?.numero_version ?? datos?.numeroVersion ?? datos?.codigo ?? datos?.versioN,
        version: payload.version,
        fecha: payload.fecha,
        fechaPublicacion: payload.fechaPublicacion,
        documento_id: payload.documento_id,
      });
    } catch (error) {
      // No bloquear la generación si la consola no está disponible.
    }

    return payload;
  }

  // GET /formatos/:formatoId/variables - Obtener variables del template
  obtenerVariablesTemplate(formatoId: number | string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${formatoId}/variables`, { withCredentials: true });
  }

  // POST /formatos/:formatoId/generar - Generar documento desde formato en BD
  generarDocumento(formatoId: number | string, datos: any): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/${formatoId}/generar`, this.normalizarPayloadDocumento(datos), {
      withCredentials: true,
      responseType: 'blob'
    }).pipe(timeout(this.requestTimeoutMs));
  }

  // POST /formatos/:formatoId/generar?tipo=word - Generar documento Word
  generarDocumentoWord(formatoId: number | string, datos: any): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/${formatoId}/generar?tipo=word`, this.normalizarPayloadDocumento(datos), {
      withCredentials: true,
      responseType: 'blob'
    }).pipe(timeout(this.requestTimeoutMs));
  }

  // POST /formatos/:formatoId/generar?tipo=pdf&inline=true - Generar PDF inline para visualizar
  generarDocumentoPdf(formatoId: number | string, datos: any): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/${formatoId}/generar?tipo=pdf&inline=true`, this.normalizarPayloadDocumento(datos), {
      withCredentials: true,
      responseType: 'blob'
    }).pipe(timeout(this.requestTimeoutMs));
  }

  // ==================== NUEVAS RUTAS: TEMPLATES SIN ID EN BD ====================

  // GET /templates - Listar templates disponibles
  listarTemplates(): Observable<any[]> {
    return this.http.get<any[]>(`${this.templatesUrl}/3`, { withCredentials: true });
  }

  listarTemplates4(): Observable<any[]> {
    return this.http.get<any[]>(`${this.templatesUrl}/4`, { withCredentials: true });
  }

  // POST /templates/:nombreArchivo/generar - Generar documento desde archivo template
  generarDocumentoDesdeArchivo(nombreArchivo: string, datos: any): Observable<Blob> {
    return this.http.post(`${this.templatesUrl}/${nombreArchivo}/generar`, this.normalizarPayloadDocumento(datos), {
      withCredentials: true,
      responseType: 'blob'
    }).pipe(timeout(this.requestTimeoutMs));
  }
}
